import React, { useCallback, useState, useEffect, useMemo, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import DataSource from 'devextreme/data/data_source'
import Form, { ButtonItem, SimpleItem, EmptyItem, ColCountByScreen, Label, GroupItem } from 'devextreme-react/form'
import 'devextreme-react/tag-box'
import JumboCardQuick from '@jumbo/components/JumboCardQuick'
import Typography from '@mui/material/Typography'
import CircularProgress from '@mui/material/CircularProgress'
import { PatternRule } from 'devextreme-react/data-grid'
import GenderTypeService from 'app/pages/registrations/customers/components/GenderTypeService'
import RelationshipTypeService from './RelationshipTypeService'
import customerServices from 'app/services/pages/reports/registrations/CustomerServices/customerServices'
import utilServices from 'app/services/util-services'
import CustomStore from 'devextreme/data/custom_store'
import { useDispatch, useSelector } from 'react-redux'
import familyAndMemberService from 'app/services/pages/reports/registrations/familyAndMemberService/familyAndMemberService'
import { useSnackbar } from 'notistack'
import { IconButton, Stack, useMediaQuery } from '@mui/material'
import WestIcon from '@mui/icons-material/West'
import { updateMember } from 'app/redux/store/features/members'
import { updateIndicator } from 'app/redux/store/features/configs'
import { getMedicalConditionTypeLookupList } from 'app/redux/store/features/medcond'
import { NO_KNOWN_MEDICAL_CONDITION_TYPE_ID } from 'app/constants/medicalConditionTypes'

const severityEditorItems = [
    { value: 0, text: 'Normal' },
    { value: 1, text: 'Medium' },
    { value: 2, text: 'High' },
    { value: 3, text: 'Critical' },
]

function normalizeSelectedMedicalTypeIds(selectedIds) {
    const raw = (selectedIds ?? []).map((id) => String(id))
    if (raw.includes(NO_KNOWN_MEDICAL_CONDITION_TYPE_ID)) return [NO_KNOWN_MEDICAL_CONDITION_TYPE_ID]
    return raw.filter((id) => id !== NO_KNOWN_MEDICAL_CONDITION_TYPE_ID)
}

function isOnlyNoKnownMedicalSelection(selectedIds) {
    const n = normalizeSelectedMedicalTypeIds(selectedIds)
    return n.length === 1 && n[0] === NO_KNOWN_MEDICAL_CONDITION_TYPE_ID
}

/** Emergency contact is optional when selection is empty or only "No known medical conditions" (after normalizing). */
function emergencyMedicalContactsOptional(selectedIds) {
    const n = normalizeSelectedMedicalTypeIds(selectedIds)
    if (n.length === 0) return true
    return n.length === 1 && n[0] === NO_KNOWN_MEDICAL_CONDITION_TYPE_ID
}

function syncMedicalAlertLevelEditor(formInstance, conditionTypeIds) {
    if (!formInstance?.getEditor) return
    const onlyNoKnown = isOnlyNoKnownMedicalSelection(conditionTypeIds)
    if (onlyNoKnown) {
        formInstance.updateData('memberMedicalSeverity', 0)
    }
    const editor = formInstance.getEditor('memberMedicalSeverity')
    if (editor) {
        editor.option('disabled', onlyNoKnown)
        editor.option('readOnly', onlyNoKnown)
    }
}

function buildMemberFormModel(member) {
    const med = member.medical ?? {}
    return {
        ...member,
        memberMedicalConditionTypeIds: (med.conditions ?? []).map((c) => c.medicalConditionTypeId),
        memberMedicalSeverity: typeof med.medicalSeverity === 'number' ? med.medicalSeverity : 0,
        memberMedicalComments: med.medicalComments ?? '',
        memberMedicalEmergencyContactName: med.medicalEmergencyContactName ?? '',
        memberMedicalEmergencyContactPhone: med.medicalEmergencyContactPhone ?? '',
    }
}

const UpdateFamilyMemberForm = (props) => {
    const navigate = useNavigate()
    const { handleSubmit } = useForm()
    const { enqueueSnackbar } = useSnackbar()
    const formRef = useRef(null)
    const memberRowId = props.memberData?.id
    const innerFormModel = useMemo(() => {
        if (!memberRowId) return null
        return buildMemberFormModel(props.memberData)
    }, [memberRowId, props.memberData])

    const [showPrimaryContact, setShowPrimaryContact] = useState(false)
    const { loading } = useSelector((state) => state.members)
    const [labelLocation, setLabelLocation] = useState('left')
    const sm = useMediaQuery('(max-width:600px)')
    const dispatch = useDispatch()
    const { lookups: medicalConditionTypeLookups } = useSelector((state) => state.medcond)

    useEffect(() => {
        dispatch(getMedicalConditionTypeLookupList({}))
    }, [dispatch])

    useEffect(() => {
        if (!memberRowId) return
        setShowPrimaryContact(!!props.memberData.isPrimary)
        // eslint-disable-next-line react-hooks/exhaustive-deps -- only when navigating to this member row
    }, [memberRowId])

    useEffect(() => {
        setLabelLocation(sm ? 'top' : 'left')
    }, [sm])

    useEffect(() => {
        if (!memberRowId || !innerFormModel) return undefined
        const timer = setTimeout(() => {
            const form = formRef.current?.instance
            if (form) syncMedicalAlertLevelEditor(form, innerFormModel.memberMedicalConditionTypeIds)
        }, 0)
        return () => clearTimeout(timer)
    }, [memberRowId, innerFormModel])

    const onMedicalFieldDataChanged = useCallback((e) => {
        if (e.dataField !== 'memberMedicalConditionTypeIds') return
        syncMedicalAlertLevelEditor(e.component, e.value)
    }, [])

    function handleError(error) {
        if (error.status === '401') {
            navigate('/profile/signout')
        } else if (error.detail) {
            enqueueSnackbar(error.detail, {
                variant: 'error',
                anchorOrigin: {
                    vertical: 'top',
                    horizontal: 'right',
                },
            })
        } else {
            enqueueSnackbar(error, {
                variant: 'error',
                anchorOrigin: {
                    vertical: 'top',
                    horizontal: 'right',
                },
            })
        }
    }

    const familyLookupList = useCallback(async (searchTerm) => {
        try {
            return await familyAndMemberService.getFamilyLookupList({ familyName: searchTerm })
        } catch (error) {
            handleError(error)
        }
    }, [])

    const familyLookupById = useCallback(async (key) => {
        try {
            return await familyAndMemberService.getFamilyLookupById(key)
        } catch (error) {
            handleError(error)
        }
    }, [])

    const familyLookupsStore = useMemo(
        () =>
            new DataSource({
                store: new CustomStore({
                    key: 'id',
                    load: async function (loadOptions) {
                        const searchTerm = loadOptions.searchValue || ''
                        return await familyLookupList(searchTerm)
                    },
                    byKey: async function (key) {
                        if (key === '') return utilServices.emptyList()
                        return await familyLookupById(key)
                    },
                    errorHandler: (e) => console.error(e),
                }),
                reshapeOnPush: true,
            }),
        [familyLookupList, familyLookupById]
    )

    const familyEditorOptions = useMemo(
        () => ({
            dataSource: familyLookupsStore,
            valueExpr: 'id',
            searchEnabled: true,
            showClearButton: true,
            disabled: true,
            itemTemplate: function (data) {
                return data ? data.familyName.toFamilyContactName(data.contactName) : ''
            },
            displayExpr: function (data) {
                return data ? data.familyName.toFamilyContactName(data.contactName) : ''
            },
        }),
        [familyLookupsStore]
    )

    const genderEditorOptions = {
        dataSource: GenderTypeService.getGenderType(),
        displayExpr: 'genderType',
        valueExpr: 'id',
        showClearButton: true,
    }

    const phoneEditorOptions = {
        valueChangeEvent: 'keyup',
        maskRules: {
            X: /\d/,
        },
    }
    const phonePattern = /^\+[1-9]\d{10,14}$/

    const primaryContactLookupList = useCallback(async (searchTerm) => {
        try {
            return await customerServices.getCustomerLookupList({ firstName: searchTerm })
        } catch (error) {
            handleError(error)
        }
    }, [])

    const primaryContactLookupById = useCallback(async (key) => {
        try {
            return await customerServices.getCustomerLookupById(key)
        } catch (error) {
            handleError(error)
        }
    }, [])

    const primaryContactLookupsStore = useMemo(
        () =>
            new DataSource({
                store: new CustomStore({
                    key: 'id',
                    load: async function (loadOptions) {
                        const searchTerm = loadOptions.searchValue || ''
                        return await primaryContactLookupList(searchTerm)
                    },
                    byKey: async function (key) {
                        if (key === '') return utilServices.emptyList()
                        return await primaryContactLookupById(key)
                    },
                    errorHandler: (e) => console.error(e),
                }),
                reshapeOnPush: true,
            }),
        [primaryContactLookupList, primaryContactLookupById]
    )

    const primaryContactEditorOptions = useMemo(
        () => ({
            dataSource: primaryContactLookupsStore,
            valueExpr: 'id',
            searchEnabled: true,
            showClearButton: true,
            itemTemplate: function (data) {
                return data ? data.firstName.toFullName(data.lastName) : ''
            },
            displayExpr: function (data) {
                return data ? data.firstName.toFullName(data.lastName) : ''
            },
        }),
        [primaryContactLookupsStore]
    )

    const medicalConditionTypesEditorOptions = useMemo(
        () => ({
            dataSource: medicalConditionTypeLookups,
            valueExpr: 'id',
            displayExpr: 'name',
            searchEnabled: true,
            showClearButton: true,
        }),
        [medicalConditionTypeLookups]
    )

    const medicalSeverityEditorOptions = useMemo(
        () => ({
            items: severityEditorItems,
            valueExpr: 'value',
            displayExpr: 'text',
        }),
        []
    )

    const onSelectionChanged = (e) => {
        setShowPrimaryContact(e.value)
    }

    const checkBoxEditorOption = {
        onValueChanged: onSelectionChanged,
    }

    const cancelButtonOptions = {
        text: 'Cancel',
        type: 'Normal',
        width: 120,
        onClick: () =>
            navigate('/registrations/families', {
                state: { navigateFrom: 'cancel-update-member' },
            }),
    }

    const submitButtonOptions = {
        text: 'Save',
        type: 'default',
        width: 120,
        useSubmitBehavior: true,
    }

    const validationRules = {
        familyName: [{ type: 'required', message: 'Family name is required.' }],
        firstName: [{ type: 'required', message: 'First name is required.' }],
        email: [{ type: 'email', message: 'Email is invalid.', ignoreEmptyValue: true }],
        genderType: [{ type: 'required', message: 'Gender type is required.' }],
        relationshipType: [{ type: 'required', message: 'Relationship is required.' }],
    }

    const relationshipEditorOptions = {
        dataSource: RelationshipTypeService.getRelationshipType(),
        displayExpr: 'relationshipType',
        valueExpr: 'id',
        showClearButton: true,
    }

    const onUpdateMember = useCallback(async () => {
        if (!innerFormModel) return
        const formInstance = formRef.current?.instance
        const currentFormData = formInstance ? formInstance.option('formData') : innerFormModel
        const {
            medical: _discardMedicalNested,
            memberMedicalConditionTypeIds,
            memberMedicalSeverity,
            memberMedicalComments,
            memberMedicalEmergencyContactName,
            memberMedicalEmergencyContactPhone,
            ...memberRest
        } = currentFormData

        const needEmergencyContact = !emergencyMedicalContactsOptional(memberMedicalConditionTypeIds ?? [])
        if (needEmergencyContact) {
            const emName = (memberMedicalEmergencyContactName ?? '').trim()
            const emPhone = (memberMedicalEmergencyContactPhone ?? '').trim()
            if (!emName) {
                enqueueSnackbar('Emergency contact name is required when medical conditions are declared.', {
                    variant: 'error',
                    anchorOrigin: { vertical: 'top', horizontal: 'right' },
                })
                return
            }
            if (!emPhone) {
                enqueueSnackbar('Emergency contact phone is required when medical conditions are declared.', {
                    variant: 'error',
                    anchorOrigin: { vertical: 'top', horizontal: 'right' },
                })
                return
            }
            if (!phonePattern.test(emPhone)) {
                enqueueSnackbar(
                    'Emergency contact phone must start with + and include country code (11–15 digits after +).',
                    {
                        variant: 'error',
                        anchorOrigin: { vertical: 'top', horizontal: 'right' },
                    }
                )
                return
            }
        }

        props.setApiCallInProgress(true)
        dispatch(updateIndicator({ inProgress: true }))
        const birthDateRaw = innerFormModel.birthDate
        let normalizedBirthDate = null
        if (!utilServices.isNullOrUndefined(birthDateRaw) && birthDateRaw !== '') {
            if (typeof birthDateRaw.toFilterStartDate === 'function') {
                normalizedBirthDate = birthDateRaw.toFilterStartDate()
            } else if (birthDateRaw instanceof Date && typeof birthDateRaw.toUtcStartOfDay === 'function') {
                normalizedBirthDate = birthDateRaw.toUtcStartOfDay()
            }
        }
        /* ISO string keeps Redux actions serializable and is what JSON bodies use anyway. */
        const birthDateForApi =
            normalizedBirthDate instanceof Date ? normalizedBirthDate.toISOString() : null
        const emNameTrimmed = (memberMedicalEmergencyContactName ?? '').trim()
        const emPhoneTrimmed = (memberMedicalEmergencyContactPhone ?? '').trim()
        dispatch(
            updateMember({
                ...memberRest,
                birthDate: birthDateForApi,
                medical: {
                    medicalComments: memberMedicalComments?.trim() ? memberMedicalComments.trim() : null,
                    medicalSeverity: isOnlyNoKnownMedicalSelection(memberMedicalConditionTypeIds ?? [])
                        ? 0
                        : typeof memberMedicalSeverity === 'number'
                          ? memberMedicalSeverity
                          : 0,
                    conditionTypeIds: normalizeSelectedMedicalTypeIds(memberMedicalConditionTypeIds ?? []),
                    medicalEmergencyContactName: needEmergencyContact ? emNameTrimmed || null : null,
                    medicalEmergencyContactPhone: needEmergencyContact ? emPhoneTrimmed || null : null,
                },
            })
        )
    }, [dispatch, enqueueSnackbar, innerFormModel, props.setApiCallInProgress])

    if (!innerFormModel) {
        return (
            <JumboCardQuick
                title={
                    <Typography color={'common.white'} variant={'h3'} sx={{ mt: 0.8 }}>
                        Update Member
                    </Typography>
                }
                headerSx={{ height: 40, bgcolor: 'primary.main' }}>
                <Stack alignItems='center' py={6}>
                    <CircularProgress />
                </Stack>
            </JumboCardQuick>
        )
    }

    return (
        <React.Fragment>
            <JumboCardQuick
                title={
                    <Stack direction='row' alignItems='center' spacing={1}>
                        <IconButton
                            className='backArrowForm'
                            onClick={() =>
                                navigate(`/registrations/families`, {
                                    state: { navigateFrom: 'cancel-update-member' },
                                })
                            }>
                            <WestIcon />
                        </IconButton>
                        <Typography color={'common.white'} variant={'h3'} sx={{ mt: 0.8 }}>
                            Update Member
                        </Typography>
                    </Stack>
                }
                headerSx={{
                    height: 40,
                    bgcolor: 'primary.main',
                }}>
                <form onSubmit={handleSubmit(onUpdateMember)}>
                    <Form
                        ref={formRef}
                        mode='form'
                        formData={innerFormModel}
                        showColonAfterLabel={false}
                        showValidationSummary={false}
                        labelLocation={labelLocation}
                        onFieldDataChanged={onMedicalFieldDataChanged}>
                        <GroupItem caption='Update Member' cssClass='form-group-item'>
                            <ColCountByScreen lg={13} md={13} sm={1} xs={1} />
                            <GroupItem />
                            <GroupItem />
                            <GroupItem />
                            <GroupItem cssClass='form-group-item' colSpan={6}>
                                <EmptyItem />
                                <SimpleItem
                                    dataField='isPrimary'
                                    editorType='dxCheckBox'
                                    editorOptions={{
                                        ...checkBoxEditorOption,
                                        text: 'Primary contact member',
                                    }}
                                />
                                {showPrimaryContact && (
                                    <SimpleItem
                                        editorType='dxSelectBox'
                                        dataField='customerId'
                                        editorOptions={primaryContactEditorOptions}>
                                        <Label text='Primary Contact' />
                                    </SimpleItem>
                                )}
                                <SimpleItem
                                    dataField='familyId'
                                    editorType='dxSelectBox'
                                    editorOptions={familyEditorOptions}>
                                    <Label text='Family' />
                                </SimpleItem>
                                <SimpleItem dataField='firstName' validationRules={validationRules.firstName} />
                                <SimpleItem dataField='lastName' />
                                <SimpleItem dataField='email' validationRules={validationRules.email}>
                                    <Label text='Email (Optional)' />
                                </SimpleItem>
                                <SimpleItem dataField='phoneNumber' editorOptions={phoneEditorOptions}>
                                    <Label text='Phone Number (Optional)' />
                                    <PatternRule
                                        message='phone number must start with + and needs to include country code'
                                        pattern={phonePattern}
                                    />
                                </SimpleItem>
                                <SimpleItem
                                    dataField='genderType'
                                    editorType='dxSelectBox'
                                    editorOptions={genderEditorOptions}
                                    validationRules={validationRules.genderType}
                                />
                                <SimpleItem
                                    editorType='dxSelectBox'
                                    dataField='relationshipType'
                                    validationRules={validationRules.relationshipType}
                                    editorOptions={relationshipEditorOptions}>
                                    <Label text='Relationship' />
                                </SimpleItem>
                                <SimpleItem
                                    dataField='birthDate'
                                    editorType='dxDateBox'
                                    editorOptions={{
                                        displayFormat: 'shortDate',
                                        type: 'date',
                                        pickerType: 'calendar',
                                        useMaskBehavior: true,
                                    }}
                                />
                            </GroupItem>
                        </GroupItem>
                        <GroupItem caption='Medical information' cssClass='form-group-item'>
                            <ColCountByScreen lg={13} md={13} sm={1} xs={1} />
                            <GroupItem />
                            <GroupItem />
                            <GroupItem />
                            <GroupItem cssClass='form-group-item' colSpan={6}>
                                <EmptyItem />
                                <SimpleItem
                                    dataField='memberMedicalConditionTypeIds'
                                    editorType='dxTagBox'
                                    editorOptions={medicalConditionTypesEditorOptions}>
                                    <Label text='Medical conditions' />
                                </SimpleItem>
                                <SimpleItem
                                    dataField='memberMedicalSeverity'
                                    editorType='dxSelectBox'
                                    editorOptions={medicalSeverityEditorOptions}>
                                    <Label text='Medical alert level' />
                                </SimpleItem>
                                <SimpleItem
                                    dataField='memberMedicalComments'
                                    editorType='dxTextArea'
                                    editorOptions={{ height: 100 }}>
                                    <Label text='Medical comments' />
                                </SimpleItem>
                                <SimpleItem dataField='memberMedicalEmergencyContactName'>
                                    <Label text='Emergency contact name' />
                                </SimpleItem>
                                <SimpleItem
                                    dataField='memberMedicalEmergencyContactPhone'
                                    editorOptions={phoneEditorOptions}>
                                    <Label text='Emergency contact phone' />
                                    <PatternRule
                                        message='Emergency contact phone must start with + and include country code'
                                        pattern={phonePattern}
                                        ignoreEmptyValue
                                    />
                                </SimpleItem>
                            </GroupItem>
                        </GroupItem>
                        <EmptyItem />
                        <GroupItem>
                            <ColCountByScreen lg={2} md={2} sm={2} xs={2} />
                            <ButtonItem
                                buttonOptions={{
                                    disabled: loading,
                                    icon: loading ? 'refresh' : 'check',
                                    ...submitButtonOptions,
                                }}
                                horizontalAlignment='right'
                            />
                            <ButtonItem buttonOptions={cancelButtonOptions} horizontalAlignment='left' />
                        </GroupItem>
                    </Form>
                </form>
            </JumboCardQuick>
        </React.Fragment>
    )
}

export default UpdateFamilyMemberForm
